<template>
  <div
    ref="balanceRef"
    class="balance"
    :class="{
      'balance-not-enough': isEnoughBalance === false
    }"
  >
    <slot name="prefix" />

    <span v-if="label && !isLoading" class="balance-label">{{ label }}:</span>
    <el-skeleton :loading="isLoading" animated class="skeleton">
      <template #template>
        <el-skeleton-item class="skeleton-item" :style="[balanceRef && `height: ${balanceRef.clientHeight - 1}px`]" />
      </template>

      <template #default>
        <UIPrice
          :icon-src="tokens[tokenMetaData(blockchain.contracts, tokenAddress).name.contract as Tokens].toString()"
          :value="displayValue"
          :is-loading="isLoading"
          :value-postfix="postfix"
        />
      </template>
    </el-skeleton>
  </div>
</template>

<script setup lang="ts">
import useBalance from '~/composables/useBalance';
import { useEnvs } from '#imports';
import tokens from 'public/img/tokens';
import { Tokens } from '~/utils/constants';
import { BigNumber } from 'bignumber.js';

const props = defineProps<{
  tokenAddress: string;
  label?: string;
  postfix?: string;
  isFormatBalance?: boolean;
  isEnoughBalance?: boolean;
  formatter?: (value: string) => string;
}>();

const balanceRef = ref<HTMLDivElement>();
const tokenAddressRef = ref(props.tokenAddress);
const { blockchain } = useEnvs();
const { balance, isLoading } = await useBalance(tokenAddressRef);

const balanceValue = computed(() => {
  if (props.tokenAddress === blockchain.contracts.usdt && balance.value) {
    return new BigNumber(balance.value).toFormat(2);
  }

  return props.isFormatBalance && balance.value ? formatAmountFloor(balance.value).toString() : balance.value;
});

const displayValue = computed(() => {
  const base = balanceValue.value ?? '';
  if (!base) return base;
  return props.formatter ? props.formatter(String(base)) : base;
});
</script>

<style scoped lang="scss">
.balance {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 48px;
  background: rgba(4, 15, 20, 0.85);
  backdrop-filter: blur(2px);
  border: 1px solid var(--blue-4);
  border-radius: 8px;
  box-shadow: 2px 4px 5px 0px rgba(0, 0, 0, 0.25) inset;
  padding: 15px;
  font-family: Eczar;
  font-weight: 500;
  font-size: 18px;
  line-height: 1;
  color: rgba(255, 255, 255, 0.5);
  position: relative;

  .balance-label {
    white-space: nowrap;
  }

  .skeleton {
    max-width: 100%;
    top: 1px;
    left: 50%;
    transform: translateX(-50%);
    position: absolute;
  }

  .skeleton-item {
    border-radius: 8px;
  }
}

.balance::v-deep(.price-value) {
  white-space: nowrap;
  font-weight: 600;
}
</style>
